import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { OutlookRecipient } from "@shared/types/resource";
import { AttendeeCalendarEvent, AttendeeInfo } from "@features/resources/services/types/attendeeEntity";

interface AttendeesState {
  requiredAttendees: AttendeeInfo[];
  optionalAttendees: AttendeeInfo[];
  allAttendees: AttendeeInfo[];
  isLoading: boolean;
  error: string | null;
  locationUnknown: string[] | null; //location is not rs and not attendee
  mainAttendee: AttendeeInfo | null; //is organizer

  setRequiredAttendees: (attendees: AttendeeInfo[]) => void;
  setOptionalAttendees: (attendees: AttendeeInfo[]) => void;
  addRequiredAttendee: (attendee: AttendeeInfo) => void;
  addOptionalAttendee: (attendee: AttendeeInfo) => void;
  removeRequiredAttendee: (email: string) => void;
  removeOptionalAttendee: (email: string) => void;
  moveToRequired: (email: string) => void;
  moveToOptional: (email: string) => void;
  clearAllAttendees: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateAllAttendees: () => void;
  setLocationUnknown: (locationUnknown: string[]) => void;
  setMainAttendee: (attendee: AttendeeInfo) => void;
  updateMeetingsAttendee: (attendee: AttendeeInfo, meetings: AttendeeCalendarEvent[]) => void;
}

const initialState = {
  requiredAttendees: [],
  optionalAttendees: [],
  allAttendees: [],
  isLoading: false,
  error: null,
  locationUnknown: null,
  mainAttendee: null,
};

export const useAttendeesStore = create<AttendeesState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setRequiredAttendees: (requiredAttendees) => {
        set({ requiredAttendees }, false, "setRequiredAttendees");
        get().updateAllAttendees();
      },

      setOptionalAttendees: (optionalAttendees) => {
        set({ optionalAttendees }, false, "setOptionalAttendees");
        get().updateAllAttendees();
      },

      addRequiredAttendee: (attendee) => {
        set(
          (state) => {
            const exists = state.requiredAttendees.some((a) => a.email.toLowerCase() === attendee.email.toLowerCase());
            if (exists) return state;

            // Remove from optional if exists
            const filteredOptional = state.optionalAttendees.filter(
              (a) => a.email.toLowerCase() !== attendee.email.toLowerCase()
            );

            return {
              requiredAttendees: [...state.requiredAttendees, attendee],
              optionalAttendees: filteredOptional,
            };
          },
          false,
          "addRequiredAttendee"
        );
        get().updateAllAttendees();
      },

      addOptionalAttendee: (attendee) => {
        set(
          (state) => {
            const exists = state.optionalAttendees.some((a) => a.email.toLowerCase() === attendee.email.toLowerCase());
            if (exists) return state;

            // Remove from required if exists
            const filteredRequired = state.requiredAttendees.filter(
              (a) => a.email.toLowerCase() !== attendee.email.toLowerCase()
            );

            return {
              optionalAttendees: [...state.optionalAttendees, attendee],
              requiredAttendees: filteredRequired,
            };
          },
          false,
          "addOptionalAttendee"
        );
        get().updateAllAttendees();
      },

      removeRequiredAttendee: (email) => {
        set(
          (state) => ({
            requiredAttendees: state.requiredAttendees.filter((a) => a.email.toLowerCase() !== email.toLowerCase()),
          }),
          false,
          "removeRequiredAttendee"
        );
        get().updateAllAttendees();
      },

      removeOptionalAttendee: (email) => {
        set(
          (state) => ({
            optionalAttendees: state.optionalAttendees.filter((a) => a.email.toLowerCase() !== email.toLowerCase()),
          }),
          false,
          "removeOptionalAttendee"
        );
        get().updateAllAttendees();
      },

      moveToRequired: (email) => {
        set(
          (state) => {
            const attendee = state.optionalAttendees.find((a) => a.email.toLowerCase() === email.toLowerCase());
            if (!attendee) return state;

            return {
              optionalAttendees: state.optionalAttendees.filter((a) => a.email.toLowerCase() !== email.toLowerCase()),
              requiredAttendees: [...state.requiredAttendees, attendee],
            };
          },
          false,
          "moveToRequired"
        );
        get().updateAllAttendees();
      },

      moveToOptional: (email) => {
        set(
          (state) => {
            const attendee = state.requiredAttendees.find((a) => a.email.toLowerCase() === email.toLowerCase());
            if (!attendee) return state;

            return {
              requiredAttendees: state.requiredAttendees.filter((a) => a.email.toLowerCase() !== email.toLowerCase()),
              optionalAttendees: [...state.optionalAttendees, attendee],
            };
          },
          false,
          "moveToOptional"
        );
        get().updateAllAttendees();
      },

      clearAllAttendees: () => {
        set(
          {
            requiredAttendees: [],
            optionalAttendees: [],
            allAttendees: [],
          },
          false,
          "clearAllAttendees"
        );
      },

      setLoading: (isLoading) => set({ isLoading }, false, "setLoading"),

      setError: (error) => set({ error }, false, "setError"),

      updateAllAttendees: () => {
        const { requiredAttendees, optionalAttendees } = get();
        const allAttendees = [...requiredAttendees, ...optionalAttendees];

        // Remove duplicates based on email
        const uniqueAttendees = Array.from(
          new Map(allAttendees.map((attendee) => [attendee.email.toLowerCase(), attendee])).values()
        );

        set({ allAttendees: uniqueAttendees }, false, "updateAllAttendees");
      },

      setLocationUnknown: (locationUnknown) => {
        set({ locationUnknown }, false, "setLocationUnknown");
      },
      setMainAttendee: (attendee) => {
        set({ mainAttendee: attendee }, false, "setMainAttendee");
      },
      updateMeetingsAttendee: (attendee, meetings) => {
        set(
          (state) => {
            if (attendee.email === state.mainAttendee?.email) {
              const newMeetings = [...state.mainAttendee?.meetings];
              const idMeetings = newMeetings.map((m) => m.id);
              meetings.forEach((m) => {
                if (!idMeetings.includes(m.id)) {
                  newMeetings.push(m);
                }
              });
              state.mainAttendee.meetings = newMeetings;
              return state;
            } else if (attendee.isRequired) {
              const index = state.requiredAttendees.findIndex(
                (a) => a.email.toLowerCase() === attendee.email.toLowerCase()
              );
              if (index !== -1) {
                const newMeetings = [...state.requiredAttendees[index].meetings];
                const idMeetings = newMeetings.map((m) => m.id);
                meetings.forEach((m) => {
                  if (!idMeetings.includes(m.id)) {
                    newMeetings.push(m);
                  }
                });
                state.requiredAttendees[index].meetings = newMeetings;
              }
            } else {
              const index = state.optionalAttendees.findIndex(
                (a) => a.email.toLowerCase() === attendee.email.toLowerCase()
              );
              if (index !== -1) {
                const newMeetings = [...state.optionalAttendees[index].meetings];
                const idMeetings = newMeetings.map((m) => m.id);
                meetings.forEach((m) => {
                  if (!idMeetings.includes(m.id)) {
                    newMeetings.push(m);
                  }
                });
                state.optionalAttendees[index].meetings = newMeetings;
              }
            }
            return state;
          },
          false,
          "updateMeetingsAttendee"
        );
        get().updateAllAttendees();
      },
    }),
    {
      name: "attendees-store",
    }
  )
);
